"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useState } from "react";
import { queryRetry, mutationRetry, exponentialBackoff } from "@/lib/hooks/auth/use-auth-aware-retry";

interface QueryProviderProps {
  children: React.ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  const [queryClient] = useState(() => {
    console.log("🔧 Initializing QueryClient with auth-aware retry configuration");

    // Global retry function: skip 4xx errors, cap at 3 attempts
    const retryUpToThreeSkip4xx = (failureCount: number, error: unknown) => {
      // Detect HTTP 4xx style errors
      const status = (error as any)?.status ?? (error as any)?.response?.status;
      if (typeof status === "number" && status >= 400 && status < 500) {
        return false;
      }
      const message = (error as any)?.message as string | undefined;
      if (message && /\b4\d{2}\b/.test(message)) {
        return false;
      }
      return failureCount < 3;
    };

    const client = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 60 * 1000, // 1 minute
          retry: retryUpToThreeSkip4xx,
          refetchOnMount: false,
          refetchOnWindowFocus: false,
          refetchOnReconnect: true,
          networkMode: "always",
        },
        mutations: {
          retry: retryUpToThreeSkip4xx,
          networkMode: "always",
        },
      },
    });

    // Add debugging for query creation
    const originalFetchQuery = client.fetchQuery;
    client.fetchQuery = function <
      TQueryFnData,
      TError = Error,
      TData = TQueryFnData,
      TQueryKey extends readonly unknown[] = readonly unknown[],
      TPageParam = never,
    >(options: any) {
      console.log("🔍 QueryClient.fetchQuery called:", {
        queryKey: options.queryKey,
        retry: options.retry,
        hasRetryFunction: typeof options.retry === "function",
      });
      return originalFetchQuery.call(this, options) as Promise<TData>;
    };

    // Query cache subscription available for debugging via DEBUG_QUERY_CACHE=true
    const shouldLogQueryCache =
      process.env.NODE_ENV === "development" && process.env.DEBUG_QUERY_CACHE === "true";
    if (shouldLogQueryCache) {
      client.getQueryCache().subscribe((event) => {
        if (event.type === "added") {
          console.log("➕ Query Added:", {
            queryKey: event.query.queryKey,
            retry: event.query.options.retry,
            hasRetryFunction: typeof event.query.options.retry === "function",
          });
        } else if (event.type === "updated") {
          console.log("🔄 Query Updated:", {
            queryKey: event.query.queryKey,
            state: event.query.state.status,
            error: event.query.state.error?.message,
            failureCount: (event.query.state as any).failureCount || 0,
          });
        }
      });
    }

    return client;
  });

  // The devtools overlay intercepts pointer events in Playwright when running
  // against `npm run dev:e2e`. Hide it via NEXT_PUBLIC_E2E_DISABLE_DEVTOOLS so
  // E2E specs do not need per-test workarounds. Set in `.env.e2e`.
  const showDevtools =
    process.env.NODE_ENV === "development" &&
    process.env.NEXT_PUBLIC_E2E_DISABLE_DEVTOOLS !== "1";

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {showDevtools && <ReactQueryDevtools initialIsOpen={true} />}
    </QueryClientProvider>
  );
}
